// Model spend for the current organisation over 30 days: spend per day and per model, from
// GET /api/orgs/{org_id}/usage/model.
import { Coins } from "@phosphor-icons/react";
import { useQuery } from "@tanstack/react-query";

import { WidgetCard } from "../ui/Card";
import { EmptyState } from "../ui/states";
import { Mono, Pill } from "../ui/status";
import { type ModelUsage, usageQuery } from "./api";
import { shortDay, usd } from "./format";
import { SpendBars } from "./lazyCharts";
import { QueryState } from "./QueryState";
import { useT } from "../i18n";

function Body({ usage }: { usage: ModelUsage }) {
  const t = useT().dashboard.spend;
  if (usage.by_day.length === 0 && usage.by_model.length === 0) {
    return (
      <EmptyState compact icon={<Coins weight="duotone" />} title={t.empty.title}>
        {t.noneBody}
      </EmptyState>
    );
  }
  const max = Math.max(...usage.by_model.map((m) => m.usd), 0.01);
  return (
    <>
      <div className="h-[150px]">
        <SpendBars data={usage.by_day.map((d) => ({ day: shortDay(d.day), usd: d.usd }))} />
      </div>
      <div className="mt-5 space-y-3">
        {usage.by_model.map((m) => (
          <div key={m.model}>
            <div className="flex items-center justify-between gap-3 text-[13px]">
              <Mono className="truncate text-gray-700">{m.model}</Mono>
              <span className="tabular font-semibold text-gray-900">{usd(m.usd)}</span>
            </div>
            <div className="mt-1.5 h-1.5 rounded-full bg-gray-100">
              <div className="h-full rounded-full bg-[rgb(var(--chart-1))]" style={{ width: `${(m.usd / max) * 100}%` }} />
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

export function ModelSpendWidget({ orgId, className }: { orgId: string | undefined; className?: string }) {
  const { dashboard } = useT();
  const t = dashboard.spend;
  const query = useQuery({ ...usageQuery(orgId ?? ""), enabled: orgId !== undefined });
  return (
    <WidgetCard
      className={className}
      title={t.title}
      label={t.title}
      description={t.description}
      action={query.data ? <Pill tone="gray">{usd(query.data.total_usd)}</Pill> : undefined}
    >
      {orgId === undefined ? (
        <EmptyState compact icon={<Coins weight="duotone" />} title={dashboard.noOrg} />
      ) : (
        <QueryState query={query} icon={<Coins weight="duotone" />} empty={t.empty}>
          {(u) => <Body usage={u} />}
        </QueryState>
      )}
    </WidgetCard>
  );
}
